import {_, axios, jsonComputedOptions, memoize, stringify} from '@api'
import {CustomAttributeDefinition, IFilterGroupItem, ImageWrapperDefinition} from '@api/graphql'
import {GroupByGroupStore, OFFLINE_FILTER_VALUE_ID, ONLINE_FILTER_VALUE_ID} from '@modules/catalog'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {FilterGroupStore, FilterGroupsItemsStore, SearchFilterStore, FilterGroupUIStore} from './components/internal'
import {ICatalogFieldConfig, nav, OFFLINE_ID, deployments, CatalogPerspectiveConfig, ui, shouldIgnoreCatalogField, LibraryItemConfigWrapper, isNumericAttribute, LOG_GLOBAL_CATALOG_LOAD, onSiteError, CatalogStore, globalStores, CatalogFilterToolbarSwitchConfig} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {removeUndefinedsNested} from '@utils'
import {ApolloQueryResult} from 'apollo-client'
import {Base64} from 'js-base64'
import {action, autorun, computed, observable, ObservableMap, reaction, runInAction, toJS, trace, when} from 'mobx'
import {computedFn, IDisposer} from 'mobx-utils'
import {SeasonSelectorFilterDropdownStore, SidebarAlign, UNASSIGNED} from '@design-board'

export const DEBUG_FILTERS = false && DEBUG
export const filterCompareFn = (a, b) => a == '' ? 1 : b == '' ? -1 : (a?.toString() || '').localeCompare(b?.toString() || '', undefined, {numeric: true, ignorePunctuation: true})

export const COMPOUND_FILTER_SEPERATOR = ';----;'
export const MAX_MORE_ITEMS = 500

export interface FilterItemState {
  id?
  exId?
  name?
}

export type FilterKey = 'search' | 'status' | 'hardware' | 'colors' | 'materials' | 'styles' | 'tag' | 'tags' | 'productComments' | 'customAttributes' | 'vlpProduct' | 'vlpSecondaries' | 'vlpVersions' | 'collectionVersion' | string

export interface IFilterValue {
  id: string,
  name?: string,
  count: number
}

export type FilterItemDefinition = SeasonalFilterItemDefinition | SimpleFilterItemDefinition

export type SeasonalFilterItemGroupState = {
  name: string
  id: string[]
  exId: string[]
  count: number
}

export interface SeasonalFilterItemDefinition extends BaseFilterItemDefinition {
  id: string[]
  exId: string[]
}

export interface SimpleFilterItemDefinition extends BaseFilterItemDefinition {
  id?: string | boolean
  exId?: string | boolean
}

export interface BaseFilterItemDefinition {
  name?: string
  label?: string
  compoundId?: string
  sectionValue?: string
  type?: string
  loading?: boolean
  error?: any
  active?: boolean
  count?: number
  status?: boolean
  hex?: string
  hsb?: { id: string, hue: number, saturation: number, brightness: number }
  images?: Array<ImageWrapperDefinition>
  grouped?: { [value: number]: Array<any> }
}

export interface FilterGroupState<ItemState = FilterItemDefinition> {
  key?: string
  customQuery?: any
  itemsStore?: FilterGroupsItemsStore
  expanded?: boolean
  everExpanded?: boolean
  selected: Map<any, ItemState>
  excluded: Map<any, ItemState>
  visibleCount?: number
  dataLoaded?: boolean
  store?: FilterGroupStore<any>

  groupedByValue: { [valueId: string]: ItemState & {count?: number} }
  def?: Partial<CustomAttributeDefinition & ICatalogFieldConfig>
  range?: { start?: number, end?: number }
  refetch?: (variables?) => Promise<ApolloQueryResult<any> | ApolloQueryResult<any>[]>

  noSearchResults?: boolean

  items?: Array<IFilterGroupItem>
  count?: number
}

export interface FilterState {
  search?: string
  /**
   * What section is selected in the section dropdown
   */
  section?: string
  sub?: Array<string>
  groups: Record<FilterKey, FilterGroupState<FilterItemDefinition>>
  more: Record<FilterKey, FilterGroupState<FilterItemDefinition>>

  extraTerms?: Array<ExtraFilterTerm>
}

/**
 * For custom filters like excluding some specific group (combo of key/values) from results
 */
export type ExtraFilterTerm = {
  query?: any
  id?: string
  store?: GroupByGroupStore
}

export interface FilterStateSerialized {
  search?: string
  section?
  extraTerms?: Array<{ id: string }>
  unassignedOnly?: boolean
  string
  groups?: {
    [groupId: string]: {
      range?: { start: number, end: number }
      expanded?: boolean,
      excluded?: string[]
      selected?: string[]
    }
  }
}

export const FILTER_UNASSIGNED_ID = 'filter_unassigned_id'

export interface FilterCollapsibleDefinition {
  globalFilters?: boolean
  specificationsFilters?: boolean
  tagsFilters?: boolean
  customAttributeFilters?: boolean
}

export interface TagAttributeMeta {
  count: number
  definition: string
  label: string
  name: string
  type: string
  values: Array<{ id: string, name: string }>
}

export type ToggleFilterParams = {
  key: FilterKey
  values?: Array<string | boolean>
  value?: string | boolean
  item?: FilterItemState
  exclude?: boolean,

  forceSet?: boolean
}

export const createFiltersForLibrary = (catalog: CatalogStore, library: LibraryItemConfigWrapper, parent?: FilterStore) => {
  if (!library) return null

  return new FilterStore(catalog, library)
}

export interface FilterWithSelection {
  state: FilterGroupState,
  filterField: string,
  group: string,
  field: ICatalogFieldConfig
}

export class FilterStore extends CatalogInstanceChildStore {
  /// region Side-Effects

  // reaction_refetchFilterDataDueToChangeInCatalog = () => reaction(() => this.state?.search, (search) => {
  //   if (this.hasLoaded) {
  //     this.loading = true
  //     const disposer = when(() => !this.runningQuery, () => {
  //       this._debouncedRefetch()
  //       disposer()
  //     })
  //   }
  // }, {name: 'Run Search filter query'})

  // /**
  //  * {@see debounceRefetchFiltersEs}
  //  */
  // private _debouncedRefetch = _.debounce(() => this.refetch(), ui.settings.filter.debounceRefetchFiltersEs)

  @computed get selectedHierarchyGroupFilters(): Map<string, Array<string>> {
    const {state: {groups}} = this
    const hierarchySelectionMap = new Map<string, Array<string>>()
    this.catalog.seasonSelectorFields.forEach(({field}) => {
      const selected = groups[field]?.selected
      if (selected) {
        const selectedNames = Array.from(selected?.keys()).map(k => selected?.get(k)?.name).filter(_.identity)
        hierarchySelectionMap.set(field, selectedNames)
      }
    })
    return hierarchySelectionMap
  }

  @computed get selectedCollectionIds() {
    const {state: {groups}} = this,
      selected = groups?.collection?.selected
    // console.log('selected collections ', selected)
    return selected ? Array.from(selected.keys()).map(k => selected.get(k)?.exId).filter(_.identity) : []
  }

  @computed get hasSearchText() {
    return _.isString(this.search) && this.search.length > 0
  }

  autorun_debounceLocalSearch = () => {
    const {es} = this

    const f = _.debounce((search: string) => {
      this.localSearchRegex = _.isEmpty(search) ? null : new RegExp(es.escapeString(search), 'i')
      this.localSearchWords = (search ?? '').split(' ') ?? []
    }, ui?.settings?.filter?.debounceLocalSearchMs ?? 100, {leading: false, trailing: true})

    return autorun(() => f(this.searchFilterStore?.search ?? this.search))
  }

  autorun_updateFilterSearch = () => {
    const f = _.debounce((search: string) => {
      // console.log(search)
      const {searchFilterStore, state} = this

      if (state.search !== searchFilterStore.search) {
        // console.log('Updating real search state')
        state.search = searchFilterStore?.search
      }
    }, ui?.settings?.filter?.debounceSearchEsMs ?? 200, {leading: false})

    return autorun(() => f(this.searchFilterStore.search), {name: 'Debounce search text to real count fetch (expensive)'})
  }

  @observable uiStores: { [group: string]: FilterGroupUIStore } = {}

  @computed get lastSearchRegex() {
    const {state: {search}, es} = this
    return _.isEmpty(search) ? null : new RegExp(es.escapeString(search), 'i')
  }

  @observable localSearchRegex: RegExp
  /**
   * Used to render highlighters when we locally know we've matched something (like a filter header)
   */
  @observable localSearchWords = []

  /*
  computed get searchWords() {
    // Intentionally use the most up to date (search store's input) while we debounce the real call to ES
    return (this.searchFilterStore?.search ?? this.state.search ?? '').split(' ') ?? []
  }
   */

  @computed get search() {
    return this.state.search || this.searchFilterStore?.search
  }

  // endregion

  @observable parsedUrl = false
  @observable loading = true

  @observable storeByLibraryId = observable.map<string, FilterStore>()
  @observable unassignedOnly = false

  /**
   * Avoid rendering filter checkboxes unless the sidebar / library for this filter set has ever been viewed
   */
  @observable everVisible = false // Needed for catalog libraries + filters support  TODO - in the future only get the group being moused over in catalog libraries

  /**
   * Avoid running library filter query until actually shown for the first time, always run the main catalog query logic once as we need the values for doing our overall query
   */
  @observable everUsed: boolean = false
  @observable.ref searchFilterStore: SearchFilterStore

  @computed get noSearchResults() {
    return this.state != null && _.isEmpty(this.groupsWithSearchResults)
  }

  @computed get groupsWithSearchResults() {
    const {state: {groups}, data, uiStores, catalog} = this
    return _.keys(data)
      .filter(k => (!uiStores[k]?.noSearchResults && k !== 'search')
                   && ((data[k]?.filterAggs ? data[k]?.filterAggs.doc_count !== 0 : data[k]?.doc_count !== 0) || !groups[k]?.noSearchResults)
                   && (!groups[OFFLINE_ID]?.noSearchResults || catalog.offlineItemCount > 0))
      .map(k => uiStores[k])
  }

  _toDispose: Array<IDisposer> = []

  @action dispose = () => this._toDispose.forEach(d => d())

  @observable initialized = false

  constructor(public catalog: CatalogStore, public library?: LibraryItemConfigWrapper) {
    super(catalog)

    // DEBUG && library && console.log(`Construct filters for library ${library.id}`, library)

    this.state = this.defaultState
    this.searchFilterStore = new SearchFilterStore(this)

    synchronizeStores(() => {
      const {stores, stores: {isInlineDetailsStore, isGlobalStore, flexLayout: {activeOrLastCatalogRoot: acr}}, _toDispose} = this


      if (isInlineDetailsStore) { this.hasLoaded = true} else if (!isGlobalStore) {
        // this.parsedUrl = true

        //  this.everVisible = true
        this.everUsed = true
      }

      if (library) {
        const queryParams = acr.config.settings.filter
        when(() => catalog.hasLoadedConfig, () => {
          this.deserializeFromQueryParams(queryParams)
        })
        const {extra} = stores
        if (extra?.parent) {
          extra?.parent.catalog.stores.filter.storeByLibraryId.set(library.id, this)
        }
      } else {
        this.everUsed = true
      }

      if (isGlobalStore || isInlineDetailsStore) return

      _toDispose.push(when(() => !this.everVisible && this.visible, () => this.everVisible = true, {name: 'Update everVisible in filter store upon first show'}))

      /** No longer do this, only create when opened
       * {@see CatalogItemsList.overrideStores}
       */
      // !library && _toDispose.push(autorun(() => {
      //   catalog.libraries.forEach(l => {
      //     const {name, filter, index} = l
      //     if (index != catalog.index && filter !== false && !this.storeByLibraryId.has(l.id)) {
      //
      //       createFiltersForLibrary(catalog, l, this)
      //     }
      //   })
      // }, {name: `Create filter stores for library filters`}))

      _toDispose.push(when(() => catalog.hasLoadedConfig && this.parsedUrl, () => {
        const {catalog, state, isLibraryFilter, library} = this
          this.state.section = this.state.section || (isLibraryFilter ? _.first(library.workspace.sections)?.id : catalog.id)
      }, {name: `YFTS-10464`}))

      _toDispose.push(autorun(() => {
        if (this.showLibraryFilterPopover && !this.visible) {
          this.showLibraryFilterPopover = false
        }
      }, {name: `YFTS-10387 close library popover when no longer visible`}))

      // _toDispose.push(autorun(() => {
      //   console.log({qp: toJS(this.queryParams), id: this.stores._id})
      // }))

      _toDispose.push(autorun(() => {
        if (!stores.authStore.userIsAuthenticated && deployments?.selectedDeploymentId) {
          this.resetFilters()
        }
      }, {name: `Clear filters on logout`}))

      _toDispose.push(reaction(() => catalog.es.refetchId, async () => {
        const {catalog: {seasonSelectorsCatalog: ss}} = this
        await Promise.all([this.refetch(true), ..._.values(ss).map(async v => await v.filterGroupStore.refetch())])
      }))

      _toDispose.push(reaction(() => this.onlyShowOfflineItems, onlyShowOfflineItems => {
        const {catalog: {settings}} = stores
        if (onlyShowOfflineItems) {
          settings.showOnlyOfflineItems = true
        } else if (settings.showOnlyOfflineItems) {
          settings.showOnlyOfflineItems = false
        }
      }, {name: 'Set offline perspective on offline filter change'}))

      // autorun(() => {
      //     const {filtersWithSelections, state, hasLoaded} = this
      //
      //   if (!hasLoaded && filtersWithSelections.length) {
      //     filtersWithSelections.forEach(async (filter) => {
      //       if (!state.more[filter.group]) {
      //         const r = await this.loadMoreData(filter.group)
      //       }
      //     })
      //   }
      // }, {name: `Load more filter data for selected filters - Sagar(?)`})

      _toDispose.push(autorun(() => {
        const {catalog: {seasonSelectorFields}, filterFields, state: {groups}} = this
        runInAction(`Setup filter groups when board configuration changes`, () => {
          [
            ...filterFields.map(({field}) => field),
            ...(seasonSelectorFields ?? []).filter(ss => ss.allowFilter !== false).map(ss => ss.field)
          ].forEach(field => !groups[field] && (groups[field] = {key: field, selected: new Map<string, FilterItemState>(), excluded: new Map<string, FilterItemState>(), expanded: false, groupedByValue: {}}))
          this.initialized = true
        })
      }, {name: `Setup filter groups when board configuration changes`}))

      _toDispose.push(autorun(async () => {
        if (this.workspaceConfig && this.everUsed) {
          const {c8: {hasLoadedHierarchy}, catalog: {workspaceConfig, hasLoadedSeasons}, stores: {flexLayoutCatalogRoot}, authStore: {userIsAuthenticated}} = this,
            {filterTerms, waitingForNestedData} = this

          if ((!workspaceConfig && !flexLayoutCatalogRoot) || !filterTerms || !userIsAuthenticated || (!this.isLibraryFilter && (!hasLoadedHierarchy || !hasLoadedSeasons))) return
          if (waitingForNestedData) return
          //console.log({catalogItemFilter, workspace, refetchId, search})

          const {catalog: {es: {refetchId, catalogItemFilter}}, c8: {services}} = this,
            {state: {sub}, refetchBody, everVisible} = this

          runInAction(async () => {
            try {
              if (!this.everVisible && _.isEmpty(this.filtersWithSelections)) { // Don't load filters until we have to
                this.hasLoaded = true
                return
              }

              await this.refetch_debounced()
            } catch (err) {
              onSiteError(err)
            } finally {
              this.loading = false
            }
          })
        }
      }, {name: 'Run filter query when context changes (different workspace, refetchId, catalog filter, search state, etc)'}))

      // this.reactionRefetchFiltersDebounced(),

      _toDispose.push(autorun(() => {
        const {data, catalog: {filteredOfflineItems}} = this,
          allOfflineData = toJS(filteredOfflineItems, {recurseEverything: true})
        this._initializeFilterGroupsFromFilterQueryResultAndOfflineItems()
      }, {name: `Populate filter values from Pending Items + query result`}))

      _toDispose.push(this.autorun_updateFilterSearch())
      _toDispose.push(this.autorun_debounceLocalSearch())
    })
  }

  @action _initializeFilterGroupsFromFilterQueryResultAndOfflineItems = () => {
    const {
      data, library, catalog, catalog: {attrKeyFor, attrValueLabelFor},
      filterFields, convertDataToFilterItems, state: {groups}
    } = this

    data && filterFields.filter(f => data[f.field]).forEach(f => {
      const {field, section} = f

      if (!groups[field]) {
        debugger
      }

      const group = groups[field],
        sectionConfig = catalog.sectionConfigFor(section),
        sectionValues = !sectionConfig?.viewOptions?.nestedView ? [] : catalog.sectionValuesFor(section),
        groupOffline = !library && catalog.groupOfflineItems(field, true) || {}

      let fieldData = data[field],
        selectedData = data[`${field}_selected`],
        r: Array<IFilterGroupItem> = [],
        returnCount = 0

      if (!sectionConfig?.viewOptions?.nestedView) {
        const r1 = convertDataToFilterItems({field: f, data: fieldData, selectedData, groupOffline})
        r.push(...r1.r)
        returnCount += r1.returnCount
      } else if (sectionConfig?.viewOptions?.field) {
        if (!_.isEmpty(sectionValues)) {
          const {doc_count: total} = fieldData
          if (fieldData.nested) { fieldData = fieldData.nested }
          if (fieldData.filterAgg) { fieldData = fieldData.filterAgg }

          sectionValues.map(({key}) => {
            const {doc_count} = fieldData[key],
              def = f.agg == 'exists' ? {type: 'Boolean'} : f?.def
            //nestedData = aggs[field.field] ?? []

            //console.log({key, field, data, total})

            if (total > doc_count) {
              r.push({
                id: attrKeyFor({key: false, def: f?.def}),
                count: total - doc_count,
                compoundId: `${key}${COMPOUND_FILTER_SEPERATOR}false`,
                name: `${key} - ${attrValueLabelFor({v: false, def})}`,
                sectionValue: key
              })
              returnCount++
            }

            if (doc_count || f?.def?.type === 'Boolean') {
              r.push({
                id: attrKeyFor({key: true, def: f?.def}),
                count: doc_count,
                compoundId: `${key}${COMPOUND_FILTER_SEPERATOR}true`,
                name: `${key} - ${attrValueLabelFor({v: true, def})}`,
                sectionValue: key
              })
              returnCount++
            }
          })
        }
      } else {
        const r1 = convertDataToFilterItems({field: f, data: fieldData, selectedData, groupOffline})
        r.push(...r1.r)
        returnCount += r1.returnCount
      }

      group.items = r
      group.count = returnCount
      group.dataLoaded = true
      const groupedByValue = _.keyBy(r.filter(n => n.count), r => r.compoundId)

      if (!_.isEqual(group.groupedByValue, groupedByValue)) {
        group.groupedByValue = groupedByValue
      }

      if (!group.def) { // If we always just set the value naively it causes an infinite loop as we create a new object every time
        group.def = {field, ...(f.def ?? f), name: field}
      }
    })
  }

  @computed get tabIndex() {
    return this.workspaceConfig?.libraries?.length > 0 ? 1 : 0
  }

  @computed get visible() {
    const {sidebar, stores: {isGlobalStore}} = this
    if (isGlobalStore) {
      return this.library ? this.showLibraryFilterPopover != false : (sidebar.left?.open && sidebar.left?.tab === this.tabIndex)
    } else {
      const {stores: {isActiveTab, purpose, flexLayout: {isShowingFilters, isShowingLibraries, activeOrLastCatalogRoot, maximizedTab}}} = this

      return purpose === 'library-items-list'
        // If maximized is the current catalog root's left sidebar open to the catalog items list item?
        // If in MDI, is the libraries panel open
             ? (maximizedTab ? maximizedTab.storesToInject?.sidebar?.left?.isLibraryItemView : isShowingLibraries)
             : isActiveTab && (isShowingFilters || isShowingLibraries) // Libraries also have filterable fields built in and require this store
    }
  }

  // region URL Serialization

  @computed get defaultState(): FilterState {
    const {isLibraryFilter, library, catalog} = this
    return {
      search: null,
      groups: {
        search: {
          selected: new Map<string, FilterItemState>(),
          excluded: new Map<string, FilterItemState>(), groupedByValue: {}
        }
      },
      more: {},
      sub: [],
      extraTerms: [],
      section: isLibraryFilter ? _.first(library.workspace?.sections ?? [])?.id : catalog.id
    }
  }

  @observable state: FilterState
  @observable hasLoaded: boolean = false

  @computed get seasonSelectorFilters(): Array<FilterGroupState> {
    const {state: {groups}, catalog: {seasonSelectorFields}} = this
    return seasonSelectorFields.map(f => groups[f.field]).filter(_.identity)
  }

  @action refetchFilterByFields = async (fields: Array<string>) => {
    if (_.size(fields)) {
      const refetchFields: Array<string> = []
      _.forEach(fields, field => {
        if (_.some(this.configuredFilterFields, cField => cField === field)) {
          refetchFields.push(field)
        }
      })
      await this.refetch(true, refetchFields)
    }
  }

  @action onToggleUnassignedOnly = () => {
    this.unassignedOnly = !this.unassignedOnly
  }

  get libraryConfig() {
    const {library} = this
    return !library ? null : this.workspaces.libraryById[library.id]
  }

  @computed get isLibraryFilter() {
    return this.library != null
  }

  @computed get workspaceConfig() {
    const {libraryConfig, catalog} = this
    return !libraryConfig ? catalog?.workspaceConfig : catalog?.workspaceByIndex[libraryConfig.index]
  }

  @computed get offlineFilter(): FilterGroupState { return this.state.groups[OFFLINE_ID]}

  @computed get onlyShowOfflineItems() {
    return this.offlineFilter?.selected.size == 1 && this.offlineFilter.selected.has(OFFLINE_FILTER_VALUE_ID)
  }

  @computed get noShowOfflineItems() {
    return this.offlineFilter?.selected.size == 1 && this.offlineFilter.selected.has(ONLINE_FILTER_VALUE_ID)
  }

  @computed get configuredFilterFieldsSet() {
    return new Set<string>(this.configuredFilterFields)
  }

  @computed get configuredFilterFields(): string[] {
    const {workspaceConfig, library, fieldByName, catalog: {_fieldByName}} = this
    const fields: ICatalogFieldConfig[] = workspaceConfig?.catalog?.fields
    let filterFields: string[] = fields
                                 ? _.toArray(fields)
                                   .filter(f => f.filter !== false && (!isNumericAttribute(f.def) || f.filter) && (f.filter != null || f.field === 'images' || (!shouldIgnoreCatalogField(workspaceConfig, f.field)) && ui.defaultCatalogFieldBehavior.filter))
                                   .map(f => f.field)
                                 : []

    //console.log(toJS({workspaceConfig, library, filterFields}, {recurseEverything:true}))
    if (!library) {
      // YFTS-7792 Filter OUT any header fields as the logic will conflict with a group name collision
      filterFields = filterFields.filter(f => !workspaceConfig?.seasonSelectors?.some(ss => ss.fields.some(ssf => ssf.field === f)))
    }

    if (library) { // Library selector popup
      // Also include season selector fields
      filterFields = [..._.flatMap<string>(workspaceConfig?.seasonSelectors?.map(s => s.fields.filter(f => f?.allowFilter !== false).map(f => f.field)) ?? []), ...filterFields]
    }

    // Remove fields that are descendent fields imported by walking FK links
    // Example is we have a field defined on style like this:
    // {
    //     field: 'attributes.rhPartner',
    //     label: 'Partner',
    //     esIndex: 'vlp-supplier',
    //     filter: {  subField: 'name' },
    //
    /// The problem is that attributes.rhPartner.name will also get imported and so you have two filters that are almost but not quite the same

    filterFields = filterFields.filter(f => {
      const lastDot = f.lastIndexOf('.')
      if (lastDot === -1) return true

      const withoutLastDot = f.substring(0, lastDot),
        parentField = fieldByName[withoutLastDot]

      if (_.isObject(parentField?.filter)) {
        if (parentField.filter.subField === f.substring(lastDot + 1)) {
          return false
        }
      }
      return true
    })

    return filterFields.filter(f => fieldByName[f]).sort()
  }

  /**
   * Data for the 'more...' poopup
   * @param group
   * @param library
   */
  @action loadMoreData = async (group, library = null) => {
    const {catalog, es} = this,
      {aggsQuery, fieldByName, workspaceConfig, filterFieldFor} = library ? this.storeByLibraryId.get(library) || this : this,
      field = fieldByName[group]

    if (!field) {
      console.error(`loadMoreData failure, could not find field ${field} in catalog configuration`)
      return
    }

    const {def} = field,
      fieldVal = filterFieldFor(field),
      a = aggsQuery({size: MAX_MORE_ITEMS, fields: [group]})

    const aggs: any = a[group] ? {[group]: a[group]} : {
      [group]: {
        filter: catalog.es.catalogItemFilterFor({ignore: {seasonal: true, plan: true, customAttributes: field.field}}).query,
        aggs: {
          items: {
            terms: {
              field: fieldVal,
              size: MAX_MORE_ITEMS,
              order: {_key: def?.type == 'Boolean' ? 'desc' : 'asc'},
              min_doc_count: def?.type == 'Boolean' ? 0 : 1    // Return both true and false results even if 0 docs are matched
            }
          },
          unassigned: {missing: {field: fieldVal}},
          count: {cardinality: {field: fieldVal}}
        }
      }
    }

    const {aggregations} = await es.search(
      removeUndefinedsNested({
        meta: 'filterStore.loadMoreData',
        body: {
          size: 0,
          ...catalog.es.catalogItemFilterFor({ignore: {customAttributes: true, vlpProduct: true, vlpVersion: true, plan: false, folder: true, collection: true, collectionVersion: true}}),
          aggs
        },
        index: workspaceConfig.catalog.index
      }),
      {log: {query: DEBUG_FILTERS, result: false}})
    // const { items: {buckets, sum_other_doc_count}} = aggregations

    const {strings, catalog: {attrKeyFor, attrValueLabelFor}} = this, {lastSearchRegex} = this
    if (aggregations) {
      const aggs_data = aggregations[field.field]?.filterAggs ?? aggregations[field.field]
      let {nested_items, count, items: {buckets}, unassigned} = aggs_data
      count = count?.value ?? 0
      unassigned = unassigned?.doc_count ?? 0
      let r = (buckets ?? []).map(({key, doc_count: count, revert}) => (
        {
          // For boolean attributes, treat unassigned values as 'false' for GUI purposes
          count: unassigned && key == 0 && field?.def?.type == 'Boolean' ? (revert?.reverse_filter?.doc_count ?? revert?.doc_count ?? count) + unassigned : (revert?.reverse_filter?.doc_count ?? revert?.doc_count ?? count),
          id: attrKeyFor({key, def: field?.def}),
          name: attrValueLabelFor({v: key, def: field?.def})
        }))

      // The keys come back with '' values first in the sorted list, this code moves such items to the end of the list
      if (r[0]?.id == '') {
        r = [...r.slice(1), r[0]]
      }

      if (unassigned && field?.def?.type != 'Boolean' && (!lastSearchRegex || strings.unassigned.match(lastSearchRegex))) {
        r.push({id: FILTER_UNASSIGNED_ID, name: strings.unassigned, count: unassigned})
      }

      // setTimeout(() => runInAction(() => {
      //   group.dataLoaded = true
      //   group.def = {...field.def, name: field.field}
      // }), 0)

      //console.log({name: def.name, esSearchClause, group: toJS(store.group)})
      this.state.more[group] = r
      return {
        data: r,
        count: count + (unassigned ? 1 : 0)
      }
    }
  }

  @computed get customAttributeFilterGroups(): Array<{ group: string, state: FilterGroupState }> {
    const {state: {groups}, catalog} = this
    if (!catalog) return []
    return _.keys(groups).filter(k => groups[k].def != null).map(k => ({group: k, state: groups[k]}))
  }

  hasNonDefaultCustomQuery = computedFn((key: string) => {
    const state = this.groupFor(key)

    if (!state) return false

    const {customQuery, range} = state

    if (customQuery) {
      if (!range) return false
      else {
        const data = this.dataFor(key)
        // console.log({data, ...range, key})

        if (range.start === range.end) return false

        if (data) {
          return data.min?.value != range.start || data.max?.value != range.end
        }
      }
    }
    return false
  })

  @computed get filtersWithSelections(): Array<FilterWithSelection> {
    const {catalog, state: {groups}, filterFieldFor, fieldByName, hasNonDefaultCustomQuery} = this
    return catalog.hasLoadedConfig ? Object.keys(groups).filter(k => hasNonDefaultCustomQuery(k) || groups[k].selected.size || groups[k].excluded.size).map(k => ({group: k, filterField: filterFieldFor(fieldByName[k]), state: groups[k], field: catalog._fieldByName[k]})) : []
  }

  @action setSelection = (key: FilterKey, value: string, item: FilterItemState) => {
    const {state: {groups}} = this,
      g = groups[key],
      {selected} = g

    selected.set(value ?? FILTER_UNASSIGNED_ID, item)
  }

  @computed get hasPendingChanges() {
    return this._pendingToggles.length > 0
  }

  private _pendingToggles = observable.array<ToggleFilterParams>([], {deep: false})

  @action commitPendingToggles = () => {
    const {_pendingToggles, toggleFilter} = this
    _pendingToggles.forEach(toggleFilter)
    this._pendingToggles.clear()
  }

  private _debounceCommitToggles = _.debounce(() => {
    this.commitPendingToggles()
  }, 500, {leading: false, trailing: true})

  @action toggleFilterBatched = (a: ToggleFilterParams) => {
    this._pendingToggles.push(a)
    if (this.catalog.isShowingClientPDF) {
      this.commitPendingToggles()
    } else {
      this._debounceCommitToggles()
    }
  }

  @action toggleFilter = ({key, value, values, item, exclude, forceSet}: ToggleFilterParams) => {
    const {toggleFilterValue} = this

    if (values) {
      values.forEach(value => toggleFilterValue({key, value, exclude, forceSet}))
    } else {
      toggleFilterValue({key, value, item, exclude, forceSet})
    }
  }

  @action toggleFilterValue = ({key, value, item, exclude, forceSet}: ToggleFilterParams) => {
    DEBUG_FILTERS && console.log(`toggleFilterValue`, toJS({key, value, item, exclude, forceSet}, {recurseEverything: true}))

    const {state: {groups}} = this,
      g = groups[key]

    if (!g) {
      console.error(`Could not find filter for key ${key}`)
      return
    }

    const {store} = g,
      map = exclude ? g.excluded : g.selected,
      v = value ?? FILTER_UNASSIGNED_ID

    if (!item) {
      item = g.groupedByValue[v.toString()]
    }

    if (forceSet) {
      map.set(v, item)
    } else {
      // If we exclude a checked item or vice versa, make sure to remove from the other set
      if (!map.has(v)) {
        (exclude ? g.selected : g.excluded).delete(v)
      }

      map.has(v) ? map.delete(v) : map.set(v, item)
    }

    if (map.size > store?.data?.entries?.length) {
      map.clear()
    }
  }

  @action toggleExpandedGroup = (group: string, forceExpand = false) => {
    const groupState = this.state.groups[group]
    groupState.expanded = forceExpand || (groupState.expanded ? false : true)
    groupState.everExpanded = groupState.expanded || groupState.everExpanded
  }

  get queryParamField() {
    const {library} = this
    return !library ? 'filter' : `filter_${library.id}`
  }

  @computed get filterTerms() {
    return this.filterTermsFor({}, {fromFilter: true})
  }

  @computed get filterTermsNested() {
    return this.filterTermsFor({}, {fromFilter: true})
  }

  @computed get filterTermsNonNested() {
    return this.filterTermsFor({}, {fromFilter: false})
  }

  isToolbarFilterSwitchSelected: ({defaultValue, field}: CatalogFilterToolbarSwitchConfig) => boolean = computedFn(({defaultValue, field}: CatalogFilterToolbarSwitchConfig) => {
    const {catalog: {settings: {toolbarSwitches}}} = this,
      v = toolbarSwitches[field],
      checked = _.isBoolean(v) ? v : (defaultValue ?? true)

    return checked
  })

  filterTermsFor: (ignore: any, other?: any) => Array<any> = (ignore: any, other: any) => {
    const {es, catalog, isToolbarFilterSwitchSelected, filtersWithSelections, hasNonDefaultCustomQuery, fieldByName, unassignedOnly, libraryConfig, state: {sub, more, groups}, library} = this,
      {catalog: {seasonSelectorFields, convertAttributeValue, index, sectionConfigFor, settings, features: {toolbarSwitches}}} = this,
      {fromFilter, isNested} = other,
      filterTerms = []

    if (!library) {
      (toolbarSwitches ?? []).forEach(switchConfig => {
        const {field, term} = switchConfig,
          selected = isToolbarFilterSwitchSelected(switchConfig)

        if (selected) {
          const esField = catalog.esFieldFor_byField(field)
          filterTerms.push(term ?? {term: {[esField]: true}})
        }
      })
    } else {
      filterTerms.push(...(library.where ?? []))
    }

    filterTerms.push(..._.keys(groups).filter(k => !ignore[k] && hasNonDefaultCustomQuery(k)).map(k => groups[k].customQuery))

    !ignore.unassigned && unassignedOnly && filterTerms.push(...[
      {
        bool: {
          must_not: {
            exists: {
              field: libraryConfig.unassignedField
            }
          }
        }
      }, {
        bool: {
          must_not: {
            term: {
              [`${libraryConfig.unassignedField}.index`]: {
                value: index
              }
            }
          }
        }
      }
    ])

    filtersWithSelections.filter(g => !ignore[g.group] && !groups[g.group].customQuery
                                      && (library || !seasonSelectorFields.find(f => (f.field == g.group || f.field === `${g.group}.name`) && !fieldByName[g.group]?.def))   // Don't include regular seasonal fields (C8 season/cat1/cat2/plan/etc) but DO include 'generic' seasonal fields we've defined ourselves YFTS-9265
                                      && (fieldByName[g.group] || fieldByName[`${g.group}.name`]) &&
                                      (!ignore.customAttributes || ignore?.customAttributes?.indexOf(g.group) === -1))
      .forEach(({group, filterField, state: {selected, excluded, groupedByValue}}) => {
        const field = fieldByName[group] ?? fieldByName[`${group}.name`],
          ssField = seasonSelectorFields.find(f => (f.field == group || f.field === `${group}.name`)),
          {def, orFields} = field,
          defType = (def?.type ?? 'String'),
          {agg} = fieldByName[group] || fieldByName[`${group}.name`] || fieldByName[`${group}.id`],
          nested = catalog.isNestedField(field),
          sectionConfig = sectionConfigFor(field.section),
          addTerm = (keys: Array<any>, map: Map<any, FilterItemState & any>, excluded = false) => {
            //console.log({filterKey, selected: Array.from(selected.keys())})
            let should = [], mustNot = []

            if (keys.length) {
              switch (defType) {
                case 'TagList': {
                  keys.length && (excluded ? mustNot : should).push({terms: {[`${group}.name`]: keys}})

                  break
                }
                case 'Tag': {
                  keys.length && (excluded ? mustNot : should).push({terms: {[filterField]: keys}})

                  break
                }
                default: {
                  if (nested) {
                    if (groupedByValue) {
                      //todo
                      (excluded ? mustNot : should).push(fromFilter ? {
                        bool: {
                          minimum_should_match: 1,
                          should: keys.map(key => {
                            let id, sectionValue,
                              groupedValue = groupedByValue?.[key] || _.find(more?.[field.field] ?? [], (f: any) => f.name == key)
                            if (groupedValue) {
                              const v = groupedValue
                              id = v.id
                              sectionValue = v.sectionValue
                            } else {
                              const v = key.toString().split(COMPOUND_FILTER_SEPERATOR, 2)
                              if (v.length != 2) {
                                DEBUG_FILTERS && console.warn(`Unable to determine key/value for ${key} for nested group ${field.field}`)
                                return null
                              }
                              sectionValue = v[0]
                              id = v[1]

                              // console.log({v, id, sectionValue})
                              if (field.def?.type === 'Boolean') {
                                id = id === 'true'
                              } else if (field.def?.type === 'Int') {
                                id = parseInt(id)
                              } else if (field.def?.type === 'Float') {
                                id = parseFloat(id)
                              }
                              // console.log({id, sectionValue})
                            }

                            const esField = catalog.esFieldFor(field)

                            return removeUndefinedsNested({
                              bool: {
                                filter: [
                                  sectionConfig.viewOptions.field && sectionConfig?.viewOptions?.nestedView ? {
                                    term: {
                                      [`${sectionConfig.path}.${sectionConfig.viewOptions.field}`]: sectionValue
                                    }
                                  } : undefined,
                                  agg === 'exists' ? (id ? (_.isEmpty(orFields) ? {
                                                           bool: {
                                                             must: [{exists: {field: esField}}],
                                                             must_not: [{term: {[esField]: ''}}]
                                                           }
                                                         } : {
                                                           bool: {
                                                             must: [{bool: {minimum_should_match: 1, should: [{exists: {field: esField}}, ...orFields.map(field => ({exists: {field}}))]}}],
                                                             must_not: [{bool: {minimum_should_match: 1, should: [{term: {[esField]: ''}}, ...orFields.map(field => ({term: {[field]: ''}}))]}}]
                                                           }
                                                         })
                                                         : {
                                                       bool: {
                                                         minimum_should_match: 1,
                                                         should: [
                                                           {bool: {must_not: [{exists: {field: esField}}]}},
                                                           {bool: {must: [{term: {[esField]: ''}}]}}
                                                         ]
                                                       }
                                                     })
                                                   : {
                                      term: {
                                        [esField]: id
                                      }
                                    }
                                ],
                                // must_not: filterFieldDef.agg === 'exists' ? undefined :  {bool: {minimum_should_match: 1, should: [{term: {[filterField]: def?.type === 'Int' ? 0 : def?.type === 'Boolean' ? !id : ''}}]}},
                                must: !sub.length ? undefined : [{terms: {[`${sectionConfig.id}.${sectionConfig.viewOptions.field}`]: sub}}]
                              }
                            })
                          }).filter(_.identity)
                        }
                      } : {
                        nested: {
                          path: sectionConfig.path,
                          query: {
                            bool: {
                              minimum_should_match: 1,
                              should: keys.map(key => {
                                let id, sectionValue,
                                  groupedValue = groupedByValue?.[key] || _.find(more?.[field.field] ?? [], (f: any) => f.name == key)
                                if (groupedValue) {
                                  const v = groupedValue
                                  id = v.id
                                  sectionValue = v.sectionValue
                                } else {
                                  const v = key.toString().split(COMPOUND_FILTER_SEPERATOR, 2)
                                  if (v.length != 2) {
                                    DEBUG_FILTERS && console.warn(`Unable to determine key/value for ${key} for nested group ${field.field}`)
                                    return null
                                  }
                                  sectionValue = v[0]
                                  id = v[1]

                                  // console.log({v, id, sectionValue})
                                  if (field.def?.type === 'Boolean') {
                                    id = id === 'true'
                                  } else if (field.def?.type === 'Int') {
                                    id = parseInt(id)
                                  } else if (field.def?.type === 'Float') {
                                    id = parseFloat(id)
                                  }
                                  // console.log({id, sectionValue})
                                }

                                const esField = catalog.esFieldFor(field)

                                return removeUndefinedsNested({
                                  bool: {
                                    filter: [
                                      sectionConfig.viewOptions.field && sectionConfig?.viewOptions?.nestedView ? {
                                        term: {
                                          [`${sectionConfig.path}.${sectionConfig.viewOptions.field}`]: sectionValue
                                        }
                                      } : undefined,
                                      agg === 'exists' ? (id ? {
                                                         bool: {
                                                           must: [{exists: {field: esField}}],
                                                           must_not: [{term: {[esField]: ''}}]
                                                         }
                                                       } : {
                                                         bool: {
                                                           minimum_should_match: 1,
                                                           should: [
                                                             {bool: {must_not: [{exists: {field: esField}}]}},
                                                             {bool: {must: [{term: {[esField]: ''}}]}}
                                                           ]
                                                         }
                                                       })
                                                       : {
                                          term: {
                                            [esField]: id
                                          }
                                        }
                                    ],
                                    // must_not: filterFieldDef.agg === 'exists' ? undefined : {bool: {minimum_should_match: 1, should: [{term: {[filterField]: def?.type === 'Int' ? 0 : def?.type === 'Boolean' ? !id : ''}}]}},
                                    must: !sub.length ? undefined : [{terms: {[`${sectionConfig.id}.${sectionConfig.viewOptions.field}`]: sub}}]
                                  }
                                })
                              }).filter(_.identity)
                            }
                          }
                        }
                      })
                    }
                  } else {
                    const termsFor = () => {
                      const termsValue = keys.map(attrValueKey =>
                        defType === 'Boolean'
                        ? attrValueKey == FILTER_UNASSIGNED_ID ? null : Boolean(attrValueKey === true || attrValueKey === 'true')
                        : attrValueKey == FILTER_UNASSIGNED_ID ? null
                                                               : attrValueKey !== 'None' ? def ? convertAttributeValue(def, attrValueKey) : attrValueKey : null
                      ).filter(v => v !== null)

                      if (termsValue.length) {
                        if (agg == 'exists') {
                          const existsField = field.esField ? catalog._fieldByName[field.esField] : null

                          if (map.has(true) || map.has('true')) {
                            if (!map.has(false) && !map.has('false')) {
                              const existsClause = _.isEmpty(orFields)
                                                   ? {exists: {field: filterField}}
                                                   : {bool: {minimum_should_match: 1, should: [{exists: {field: filterField}}, ...orFields.map(field => ({exists: {field}}))]}}

                              return existsField?.def?.type === 'String'
                                     ? {
                                  bool: {
                                    filter: [existsClause],
                                    must_not: {term: {[field.esField]: ''}}
                                  }
                                }
                                     : existsClause
                            }
                          } else if (map.has(false) || map.has('false')) {
                            const notClause: any = _.isEmpty(orFields)
                                                   ? {bool: {must_not: {exists: {field: filterField}}}}
                                                   : {bool: {minimum_should_match: 1 + orFields.length, should: [{bool: {must_not: {exists: {field: filterField}}}}, ...orFields.map(field => ({bool: {must_not: {exists: {field}}}}))]}}

                            return existsField?.def?.type === 'String'
                                   ? {
                                bool: {
                                  minimum_should_match: 1,
                                  should: [
                                    notClause,
                                    {term: {[field.esField]: ''}}
                                  ]
                                }
                              }
                                   : notClause
                          }
                        } else {
                          if (excluded && defType === 'Boolean' && termsValue.every(v => v == false || v == 'false')) {
                            keys.length && should.push({
                              'exists': {
                                field: filterField
                              }
                            })
                          }

                          return {
                            terms: {
                              [filterField]: _.flatMap(termsValue).filter(f => f !== null)
                            }
                          }
                        }
                      }
                    }
                    const terms = termsFor()
                    keys.length && terms && (excluded ? mustNot : should).push(terms)
                  }
                }
                  break
              }
            }

            if (map.has(FILTER_UNASSIGNED_ID)) {
              if (agg == 'exists') {
                if (map.has(false) && !map.has(true)) {
                  (excluded ? should : mustNot).push({exists: {field: filterField}})
                }
              } else {
                (excluded ? should : mustNot).push({
                  exists: {field: filterField}
                })
              }
            }

            if (should || mustNot) {
              const term = es.compoundBoolTermsFor(should, mustNot, excluded)
              term && !isNested && filterTerms.push(term)
            }
          }

        selected.size && addTerm(Array.from(selected.keys()), selected, false)
        excluded.size && addTerm(Array.from(excluded.keys()), excluded, true)
      })

    return removeUndefinedsNested(filterTerms)
  }

  /***
   * Computes a base64 encoded filter query parameter that contains the state of the filter system (selection, expansion, and search)
   */
  @computed.struct get queryParams() {
    const {catalog, queryParamField} = this

    // Serialize a minified version of our state graph
    const {state, unassignedOnly, storeByLibraryId} = this

    let filterSerialized: any = {}
    state.search && (filterSerialized.search = state.search)
    state.section && (filterSerialized.section = state.section)
    filterSerialized.groups = {}
    filterSerialized.unassignedOnly = unassignedOnly ? unassignedOnly : undefined
    filterSerialized.extraTerms = (state.extraTerms ?? []).map(t => ({id: t.id}))

    _.keys(state.groups).forEach(g => {
      const {groups} = state,
        originalGroup = groups[g],
        {range, expanded, selected, excluded} = originalGroup

      let filterGroupSerialized: any = {}
      // Commenting below if condition as if we do this in this store another issue comes which is if
      // filter is expanded in pdf view and refreshed it will stay collapsed as it is never gets added in url
      // instead moved logic in PDFUndoStore so we can filter out expanded flag logic for PDF view only
      // if (!pdf.show) { // YFTS-6240 - we don't want to trigger undo behavior when groups expand/collapse
      expanded && (filterGroupSerialized.expanded = expanded)
      // }
      selected.size && (filterGroupSerialized.selected = Array.from(selected.keys()))
      excluded.size && (filterGroupSerialized.excluded = Array.from(excluded.keys()))
      range && (filterGroupSerialized.range = range)

      !_.isEmpty(filterGroupSerialized) && (filterSerialized.groups[g] = filterGroupSerialized)
    })
    _.isEmpty(filterSerialized.groups) && delete filterSerialized.groups
    _.isEmpty(filterSerialized.extraTerms) && delete filterSerialized.extraTerms
    filterSerialized = removeUndefinedsNested(filterSerialized)

    const r = {
      [queryParamField]: _.isEmpty(filterSerialized) ? undefined : Base64.encodeURI(stringify(filterSerialized)),
      ...Array.from(storeByLibraryId.values()).reduce((acc, k) => ({...acc, ...k.queryParams}), {})
    }
    // console.log({filterSerialized, r})
    return r
  }

  lastQueryParamsParsed

  @action deserializeFromQueryParams = (filterParams) => {
    _.merge(this.state, this.defaultState)
    this.parsedUrl = true
    const {queryParamField, catalog, isLibraryFilter, library} = this,
      parsed = filterParams && filterParams[queryParamField] ? JSON.parse(Base64.decode(filterParams[queryParamField])) as FilterStateSerialized : null

    DEBUG_FILTERS && console.log(`deserializeFromQueryParams`, {f: this, filterParams: toJS(filterParams), parsed})
    if (!parsed && !this.lastQueryParamsParsed && this.stores.isGlobalStore) return

    // This is causing filters not to load when rapidly switching between reports
    // if (_.isEqual(this.lastQueryParamsParsed, parsed)) {
    //   if (_.isEmpty(parsed) && this.hasFilterTerms) {
    //     DEBUG_FILTERS && console.debug(`Resetting filter terms - parsed is empty`)
    //     this.reset()
    //   } else {
    //     DEBUG_FILTERS && console.debug(`Ignoring parsed due to equal with last`, {parsed: toJS(parsed), lastQueryParamsParsed: this.lastQueryParamsParsed})
    //     return
    //   }
    // }

    this.lastQueryParamsParsed = parsed

    // if (filter) {
    //   this.setFilter(JSON.parse(filter))
    // } else if (resetIfNotSet) {
    //   this.reset()
    // }
    const {state} = this
    if (parsed) {
      try {
        state.search = parsed.search || state.search
        state.section = parsed.section ||state.section
        state.extraTerms = parsed.extraTerms ?? []
        this.unassignedOnly = parsed.unassignedOnly === true
        // We have an internal map of id -> filterItem, but we only want to serialize off an array of ids
        _.keys(parsed.groups).filter(k => this.configuredFilterFieldsSet.has(k) || catalog.seasonSelector.seasonSelectorsSet.has(k)).forEach(k => {
          if (!state.groups[k]) {
            DEBUG_FILTERS && console.debug(`Creating missing group state for ${k}`)

            state.groups[k] = {key: k, selected: new Map<string, any>(), excluded: new Map<string, any>(), groupedByValue: {}}
          }

          const g = state.groups[k]

          if (!parsed.groups || !parsed.groups[k] || !parsed.groups[k].selected) {
            g.selected.clear()
          } else {
            if (!g.selected) { g.selected = new Map<string, FilterItemState>() }
            const existing = new Set<string>(g.selected.keys())

            parsed.groups[k].selected.forEach(id => {
              (!g.selected.has(id)) && g.selected.set(id, null)
              existing.delete(id)
            })

            // Remove any remaining selected items not on the URL
            Array.from(existing.keys()).forEach(k => g.selected.delete(k))
          }

          if (!parsed.groups || !parsed.groups[k] || !parsed.groups[k].excluded) {
            g.excluded.clear()
          } else {
            if (!g.excluded) { g.excluded = new Map<string, FilterItemState>() }
            const existing = new Set<string>(g.excluded.keys())

            parsed.groups[k].excluded.forEach(id => {
              (!g.excluded.has(id)) && g.excluded.set(id, null)
              existing.delete(id)
            })

            // Remove any remaining selected items not on the URL
            Array.from(existing.keys()).forEach(k => g.excluded.delete(k))
          }

          g.range = parsed.groups[k].range
          if (g.range) {
            g.customQuery = this.rangeQueryTermFor({field: k, ...g.range})
          }
          g.expanded = parsed.groups && parsed.groups[k] && parsed.groups[k].expanded
          g.everExpanded = g.expanded || g.everExpanded
        })
      } catch (err) {
        console.error(err)
      }
    } else {
      // Clear all filters
      state.search = undefined
      state.section = isLibraryFilter ? (_.first(library?.workspace?.sections)?.id) : catalog.id
      state.extraTerms = []
      state.sub = []
      _.values(state.groups).forEach(g => {
        g.selected.clear()
        g.excluded.clear()
      })
    }
  }

  rangeQueryTermFor = ({field, start, end}: { field: string, start?: number, end?: number }) => {
    if (!_.isNumber(start) && !_.isNumber(end)) return undefined

    return start === end ? null : ({
      bool: {filter: [{range: {[field]: removeUndefinedsNested({boost: 2, gte: _.isNumber(start) ? start : undefined, lte: _.isNumber(end) ? end : undefined})}}]} //, {bool: {must_not: {exists: {field: group}}
    })
  }

  // endregion

  @computed get hasFilterTerms(): boolean {
    const {catalog, state: {groups, extraTerms}, library, hasNonDefaultCustomQuery} = this,
      {settings: {freezeIds}, isShowingPerspective} = catalog
    return _.some(_.keys(groups).filter(g => library || !catalog.isSeasonSelectorField(g)).map(g => groups[g].selected?.size > 0 || groups[g].excluded?.size > 0 || hasNonDefaultCustomQuery(g))) || (!library && !isShowingPerspective && freezeIds.length > 0) || (!library && catalog.perspective?.filter != null) || !_.isEmpty(extraTerms)
  }

  @action resetFilterExcludeSeason = () => {
    this.resetFilters(false)
  }

  @action reset = () => {
    const {state, defaultState} = this
    this.resetFreezeIds()

    if (state) {
      state.section = defaultState.section
      state.search = null
      this.filtersWithSelections.forEach(f => /*!_fieldByName[f.group] &&*/ {
        f.state.selected.clear()
        f.state.excluded.clear()
      })
    }

    this.data = null
  }

  @action resetFilters = (includeSeasonFields = true) => {
    // this.state = this.defaultState
    const {state, library, catalog} = this
    // state.search = this.search
    state.extraTerms = []
    this.data = null
    Object.keys(state.groups).forEach(k => {
      if (library || includeSeasonFields || !catalog.seasonSelectorFields.some(f => f.field == k)) {
        this.clearSelection(k)
      }
    })

    if (!this.library) {
      this.resetFreezeIds()

      if (catalog.perspective?.filter) {
        catalog.setPerspective(_.first(catalog.perspectives))
      }
    }
  }

  @action clearSelection = (key) => {
    const g = this.state.groups[key]

    if (g) {
      g.selected.clear()
      g.excluded.clear()
      g.range = null
    }
  }

  @action remove = (key, value: any, exclude = false) => {
    // const {state: {groups}} = this
    // groups[key].selected.delete(value)
    this.toggleFilter({key, value, exclude})
  }

  @action resetFreezeIds = () => {
    this.catalog.selection?.resetFreezeIds()
  }

  // region ES Queries

  @computed get filterAttributes() {
    const {catalog: {attrForEsField}} = this, {configuredFilterFields} = this
    return _.uniq([...configuredFilterFields]).map(attrForEsField).filter(v => v)
  }

  @computed get fieldByName(): { [field: string]: ICatalogFieldConfig } {
    const {catalog} = this
    return !this.libraryConfig ? catalog._fieldByName : catalog.fieldByWorkspaceIndex[this.libraryConfig.index]?.fields
  }

  @computed get filterFields(): ICatalogFieldConfig[] {
    const {configuredFilterFields, catalog, fieldByName} = this
    return _.orderBy(
      _.uniq(configuredFilterFields).map(f => fieldByName[f]),
      f => /*f?.order ??*/ catalog.labelForField(f))
    // .filter(f => f.section != null) FIXME - library filters are not getting loaded with this filter.
  }

  @computed get expandedGroups(): string[] {
    const {state: {groups}} = this

    return _.keys(groups).filter(k => groups[k].expanded)
  }

  @observable.ref data = null

  filterFieldFor = (field: ICatalogFieldConfig) => {
    if (!field) return null

    const {filter} = field, {catalog: {workspaceForField}} = this,
      subField = _.isBoolean(filter) ? null : (filter?.subField ?? (!filter && field.esIndex ? workspaceForField(field)?.dataMap?.title ?? 'name' : null))
    const fieldVal = this.catalog.esFieldFor(field)

    return `${fieldVal}${subField ? `.${subField}` : ''}`
  }

  @computed get noValueSearch() {
    const {strings, state: {search}} = this
    return !_.isEmpty(search) && strings.noValue.toLowerCase().indexOf(search.toLowerCase()) != -1
  }

  @computed get waitingForNestedData() {
    const {catalog, filterFields} = this
    return filterFields.some(({section}) => {
      if (!section) return false
      const sectionConfig = catalog.sectionConfigFor(section)

      if (!sectionConfig?.viewOptions?.field || sectionConfig.visible === false) return false

      const sectionValues = catalog.sectionValuesFor(section)
      return !sectionValues
    })
  }

  /**
   * Selected filter values are translated into 'filter' fields on individual ES aggregations
   * This is so that selecting a value for one category doesn't filter out other unselected values for the same filter group
   */
  @computed get aggs() {
    // trace()
    return this.aggsQuery({})
  }

  aggsQuery = ({size, fields}: { size?: number, fields?: Array<string> }) => {
    // trace()
    //  console.debug(`aggsQuery`, this.stores._id, {size, fields, library: this.library?.id})
    const {es, catalog, filterFields, filterFieldFor, noValueSearch, state: {search, groups}, library, filterTermsFor, lastSearchRegex} = this,
      maxValuesToLoad = ui.settings.filter.maxValuesToLoadPerGroup

    let filteredFields = fields?.length ? filterFields.filter(f => fields.includes(f.field)) : filterFields,
      filteredFieldsNonNested = filteredFields.filter(({section}) => !catalog.sectionConfigFor(section)?.viewOptions?.field)

    let r = removeUndefinedsNested(
      filteredFieldsNonNested.reduce((acc, f) => {
        // TODO - move this inner statement into a computedFn
        const {agg, field, esIndex, def, orFields} = f,
          filterEsField = filterFieldFor(f), // Something like color.name,
          esField = catalog.esFieldFor(f) // Something like just color

        let clause, selectedClause,
          aggName = field

        switch (agg) {
          case 'exists': {
            if (_.isEmpty(orFields)) {
              //{filter: {bool: {filter: [{exists: {field: queryField}}], must_not: {term: {[queryField]: ''}}}}}
              clause = {items: {filter: {bool: {filter: {exists: {field: filterEsField}}, must_not: {term: {[filterEsField]: ''}}}}}}
            } else {
              clause = {items: {filter: {bool: {minimum_should_match: 1, should: [{exists: {field: filterEsField}}, ...orFields.map(field => ({exists: {field}}))]}}}}
            }
            break
          }

          default : {
            const selected = groups[field]?.selected

            clause = {
              unassigned: {missing: {field: filterEsField}}
            }

            _.assign(clause, isNumericAttribute(def) ? {
              max: {
                max: {
                  field: filterEsField
                }
              },
              min: {
                min: {
                  field: filterEsField
                }
              },
              avg: {
                avg: {
                  field: filterEsField
                }
              },
              percentiles: {
                percentiles: {
                  field: filterEsField
                }
              }
            } : {
              count: {cardinality: {field: filterEsField}},
              items: {
                terms: {
                  field: filterEsField,
                  size: size ? size : def?.type == 'TagList' || def?.type === 'EnumList' || f.sort?.custom || f.sort?._func ? 100000 : maxValuesToLoad,
                  exclude: Array.from(selected?.keys() ?? []).filter(k => k != FILTER_UNASSIGNED_ID),
                  order: {_key: def?.type == 'Boolean' ? 'desc' : 'asc'},
                  min_doc_count: def?.type == 'Boolean' ? 0 : 1    // Return both true and false results even if 0 docs are matched
                },
                aggs: esIndex && esField ? {
                  node: {top_hits: {size: 1, _source: esIndex ? [`${esField}.images`, `${esField}.hex`] : []}}
                } : undefined
              }
            })

            if (selected?.size) {
              selectedClause = {
                items: {
                  terms: {
                    field: filterEsField,
                    size: selected.size,
                    include: Array.from(selected.keys()).filter(k => k != FILTER_UNASSIGNED_ID),
                    order: {_key: def?.type == 'Boolean' ? 'desc' : 'asc'},
                    min_doc_count: 0
                  },
                  aggs: filterEsField && esIndex ? {
                    node: {top_hits: {size: 1, _source: [`${esField}.images`, `${esField}.hex`]}}
                  } : undefined
                }
              }
            }
            break
          }
        }

        // TODO - why do we include all the seasonal where clauses on every aggregation filter instead of just once at the top of the overall query?
        const filter = !library ? catalog.es.catalogItemFilterFor({ignore: {seasonal: true, [field]: true}}).query
                                : {bool: {filter: filterTermsFor({[field]: true}, true).flat()}}
        return Object.assign(acc, {
          [aggName]: {
            // Removed Mahesh's code from YFTS-8611 as it was breaking search
            //  || !_.isEmpty(f.sort?.custom?._key?.order) || !_.isEmpty(f.def?.enumValues)
            filter: _.merge(
              {},
              filter,
              _.isEmpty(search) || def?.type === 'Boolean' || noValueSearch || catalog.labelForField(f).match(this.lastSearchRegex)
              ? {}
              : {
                  bool: {
                    filter: _.isObject(f.filter) && f.filter.esWhere ? f.filter.esWhere.flat() : undefined,
                    must: [
                      {
                        query_string: {
                          fuzziness: 'AUTO',
                          query: `${es.tokenizeQueryString(search, false)}*`,
                          fields: [`${filterEsField}.text`],
                          default_operator: 'AND'
                        }
                      }
                    ]
                  }
                }),
            aggs: clause
          },
          [`${field}_selected`]: selectedClause ? {
            filter,
            aggs: selectedClause
          } : undefined
        })
      }, {})
    )

    const subNestedFields = filteredFields.filter(({section, field}) => {
        const s = catalog.sectionConfigFor(section) || catalog.sectionConfigFor(field)
        return s?.viewOptions?.field && !s?.viewOptions?.nestedView
      }),
      subSections = _.groupBy(subNestedFields, f => f.section ?? f.field),
      {state: {sub}} = this

    r = _.assign(r, _.keys(subSections).reduce((acc, section) => {
      const fields = subSections[section],
        sectionConfig = catalog.sectionConfigFor(section),
        f = `${sectionConfig.id}.${sectionConfig.viewOptions.field}`,
        {filtersWithSelections} = this,
        sectionValues = catalog.sectionValuesFor(section),
        sectionValueAggs = sectionValues && sectionConfig?.viewOptions?.field ? sectionValues.reduce((sectionValueAggs, sv) => {
          return sv.key ? {...sectionValueAggs, [sv.key]: {filter: {term: {[`${sectionConfig.path}.${sectionConfig.viewOptions.field}`]: sv.key}}}} : undefined
        }, {}) : {}

      return _.assign(
        acc,
        fields.reduce((total, fieldDefinition) => {
          // TODO - move this inner reduce into a computed function
          const {field, esField, agg, def, filter: fieldFilter} = fieldDefinition,
            isNested = catalog.isNestedField(fieldDefinition),
            haveField = _.isEmpty(filtersWithSelections.find(f => f.group === field))
          let filter = isNested ? this.filterTermsFor({[field]: true}, {fromFilter: true, isNested: true}) : this.filterTermsFor({[field]: true}, {fromFilter: false})
          if (sub?.length) {
            filter = [
              ...(filter ?? []), {
                terms: {
                  [f]: sub
                }
              }
            ]
          }

          // Use the filter clause from one of the non-nested filters first
          const otherAggsQueryFilter = r[_.first(filteredFieldsNonNested).field].filter
          const queryField = esField ?? this.filterFieldFor(fieldDefinition),
            fieldAggClause = {
              [field]: {
                filter: otherAggsQueryFilter,
                aggs: {
                  nested: {
                    nested: {
                      path: section
                    },
                    aggs: {
                      filterAggs: {
                        filter: {
                          bool: {
                            filter: _.concat(filter, (_.isObject(fieldFilter) && fieldFilter?.esWhere && _.isArray(fieldFilter.esWhere)) ? fieldFilter.esWhere : []).flat(),
                            ..._.isEmpty(search) || def?.type === 'Boolean' || noValueSearch || catalog.labelForField(fieldDefinition).match(lastSearchRegex)
                               ? {}
                               : {
                                must: [
                                  {
                                    query_string: {
                                      fuzziness: 'AUTO',
                                      query: `${es.tokenizeQueryString(search, false)}*`,
                                      fields: [`${queryField}.text`],
                                      default_operator: 'AND'
                                    }
                                  }
                                ]
                              }
                          }
                        },
                        aggs: {
                          items: {
                            ...(
                              agg === 'exists' ? {filter: {bool: {filter: [{exists: {field: queryField}}], must_not: {term: {[queryField]: ''}}}}}
                                               : {terms: {field: queryField, size}}
                            ),
                            aggs: {
                              revert: {
                                reverse_nested: {},
                                aggs: this.filterTerms.length ? {
                                  reverse_filter: {
                                    filter: {
                                      bool: {
                                        filter: haveField ? this.filterTerms[0] : []
                                      }
                                    }
                                  }
                                } : undefined
                              },
                              ...sectionValueAggs
                            }
                          },
                          count: {cardinality: {field: queryField}},
                          unassigned: {missing: {field: queryField}},
                          total: agg === 'exists' ? {reverse_nested: {}} : undefined
                        }
                      }
                    }
                  }
                }
              }
            }

          // console.log({haveField})
          return removeUndefinedsNested({
            ...total,
            ...fieldAggClause
          })
        }, {})
      )
    }, {}))

    const nestedFields = filteredFields.filter(({section}) => {
        const s = catalog.sectionConfigFor(section)
        return !s?.viewOptions?.field && s?.viewOptions?.nestedView
      }),
      sections = _.groupBy(nestedFields, f => f.section)

    r = _.assign(
      r,
      _.keys(sections).reduce(
        (acc, section) => {
          const {path, viewOptions} = catalog.sectionConfigFor(section),
            sectionValues = catalog.sectionValuesFor(section)

          if (_.isEmpty(sectionValues)) return acc

          const fieldsInSection = sections[section],
            sectionValueAggs = sectionValues.reduce((sectionValueAggs, sv) => {
              return {...sectionValueAggs, [sv.key]: {filter: {term: {[`${path}.${viewOptions.field}`]: sv.key}}}}
            }, {})

          return _.assign(
            acc,
            fieldsInSection.reduce((sectionFieldAggs, fieldDef) => {
              const esField = catalog.esFieldFor(fieldDef),
                filterTerms = this.filterTermsFor({[fieldDef.field]: true}, {fromFilter: true})

              return _.assign(sectionFieldAggs, {
                [fieldDef.field]: {
                  filter: {bool: {must: filterTerms}},
                  aggs: {
                    nested: {
                      nested: {path},
                      aggs: {
                        filterAgg: {
                          filter: fieldDef.agg === 'exists' ? {
                            bool: {
                              must: [{exists: {field: esField}}],
                              must_not: [{term: {[esField]: ''}}]
                            }
                          } : fieldDef?.def?.type === 'Boolean' ? {term: {[esField]: true}}
                                                                : {exists: {field: esField}},
                          aggs: sectionValueAggs
                        }
                      }
                    }
                  }
                }
              })
            }, {}))
        }
        , {}),

      // todo:  need to reverse this for YFTS-6333, need to be field -> secondary values instead of secondary values -> field to support cross-filtering between secondary fields
      (true ? {} : _.keys(sections).reduce(
        (acc, section) => {
          const {path, viewOptions} = catalog.sectionConfigFor(section),
            sectionValues = catalog.sectionValuesFor(section)

          if (_.isEmpty(sectionValues)) return acc

          const fieldsInSection = sections[section]

          return _.assign(acc, {
            [section]: {
              nested: {
                path
              },
              aggs: sectionValues.reduce((sectionAggs, v) => {
                const fieldPath = `${path}.${viewOptions.field}`
                return {
                  ...sectionAggs,
                  [v.key]: {
                    filter: {term: {[fieldPath]: v.key}},
                    aggs: fieldsInSection.filter(f => catalog.esFieldFor(f) != fieldPath).reduce((sectionFields, fieldDef) => {
                      const field = catalog.esFieldFor(fieldDef),
                        filterTerms = [] // this.filterTermsFor({[fieldDef.field]: true}) // YFTS-6333

                      if (fieldDef.agg === 'exists') {
                        return {
                          ...sectionFields,
                          [fieldDef.field]: {
                            filter: {
                              bool: {
                                must: [{exists: {field}}, ...filterTerms],
                                must_not: [{term: {[field]: ''}}]
                              }
                            }
                          }
                        }
                      } else {
                        const agg = {
                          terms: {
                            field,
                            size: 100
                          }
                        }

                        //console.log(fieldDef.field, filterTerms)

                        if (_.isEmpty(filterTerms)) {
                          return {
                            ...sectionFields,
                            [fieldDef.field]: agg
                          }
                        } else {
                          return {
                            ...sectionFields,
                            [fieldDef.field]: {
                              filter: {
                                bool: {
                                  filter: filterTerms
                                }
                              },
                              aggs: {
                                items: agg
                              }
                            }
                          }
                        }
                      }
                    }, {})
                  }
                }
              }, {})
            }
          })
        }, {}))
    )

    return r
  }

  @observable private runningQuery = false

  private prevCatalogIndex: string = ''

  refetchBodyForFields(fieldKeys: Array<string> = []) {
    const aggs = this.refetchBody.aggs
    return {
      ...this.refetchBody,
      aggs: fieldKeys.length ? fieldKeys.reduce((a, f) => Object.assign(a, {[f]: aggs[f], [`${f}_selected`]: aggs[`${f}_selected`]}), {}) : aggs
    }
  }

  @computed get filterQueryClause() {
    const {catalog, library} = this

    const r = catalog.es.catalogItemFilterFor(!library ? {ignore: {customAttributes: true}} : {ignore: {plan: true}}) //customAttributes: true, vlpProduct: true, vlpVersion: true
    return r
  }

  @computed get refetchBody() {
    // trace()
    const {catalog, aggs, library, libraryConfig, filterQueryClause} = this,
      {es: {catalogItemFilterFor}} = catalog
    // fields = _.differenceBy(
    //   Object.keys(this.state.groups).map(field => ({
    //     field, subField: (this.state.groups[field].def?.filter as any)?.subField
    //   })),
    //   ['OFFLINE!', 'search', ...stores.catalog.seasonSelectorsFields.map(s => s.field)]
    //     .map(field => ({field, subField: ''})), 'field')

    return {
      size: 0,
      ...(
        {
          query: {
            ...filterQueryClause.query,
            bool: {
              ...(library ? {filter: [...(catalog.whereForLibrary(libraryConfig) ?? [])].flat().filter(_.identity)} : filterQueryClause.query.bool)
              // must: _.isEmpty(this.state?.search) ? [] : {
              //   query_string: {
              //     query: `*${es.tokenizeQueryString(this.state?.search, true)}*`,
              //     fields: fields.map(({field, subField}) => `${field}${subField ? `.${subField}` : ''}.text`),
              //     default_operator: 'AND'
              //   }
              // }
            }
          }
        }
      ),
      aggs
    }
  }

  // @observable.ref cancelToken: CancelTokenSource

  refetch_debounced = _.debounce(() => this.refetch(), 100)

  private _lastRefetchBody: any
  @action refetch = async (force: boolean = false, specificFields: Array<string> = []) => {
    if (this.stores.purpose === 'inline-details' || !this.catalog.catalogConfig?.index) return

    const {
      workspaces, es, catalog: {fieldsWithCustomSorts, es: {catalogItemFilter}}, catalog,
      workspaceConfig, runningQuery, library, parsedUrl, flexLayoutCatalogRoot, stores
    } = this

    if (!workspaces.current && !flexLayoutCatalogRoot) return

    if (!library && this.prevCatalogIndex !== catalog.index) {
      this.runningQuery = false
      if (this.prevCatalogIndex) {
        this.reset() // Clear existing filters when workspace changes
      }

      // if (this.cancelToken) {
      //   this.cancelToken.cancel(`Cancel filter search - workspace changed`)
      // }
    }

    if (!workspaceConfig || runningQuery || (!library && (!parsedUrl || !catalogItemFilter))) {
      ///console.debug(`skipping filter refetch`, {workspaceConfig, runningQuery, parsedUrl, catalogItemFilter })
      return null
    }

    const {refetchBody} = this,
      refetchBodyForFields = specificFields.length ? this.refetchBodyForFields(specificFields) : {}

    // Finicky - YFTS-10461
    // if (!force && refetchBody != this._lastRefetchBody && !specificFields.length && this.data != null) {
    //   LOG_GLOBAL_CATALOG_LOAD && console.debug(`Filter query hasn't changed, skipping`)
    //   this.loading = false
    //   this.runningQuery = false
    //   return
    // }

    DEBUG_FILTERS && DEBUG && console.debug(`Running Filters ES Query for ${library ? `${library.id} ` : ''} - ${this.stores._id}  [START]`, refetchBody, this.stores._id)

    runInAction(() => {
      this.prevCatalogIndex = workspaceConfig.catalog.index
      this._lastRefetchBody = refetchBody
      this.loading = true
      this.runningQuery = true
    })
    // this.cancelToken = axios.CancelToken.source()

    try {
      const {aggregations} = await es.search(
        removeUndefinedsNested({
          // cancelToken: this.cancelToken.token,
          meta: `filterStore.refetch(${library?.id ?? library?.name ?? catalog.index})${specificFields ? ` ${specificFields}` : ''}`,
          body: specificFields.length ? refetchBodyForFields : refetchBody, index: workspaceConfig.catalog.index
        }),
        {log: {query: false, result: false}})

      // Short-circuit if this result is for a previous query
      if (!this.runningQuery) return

      //console.log(aggregations)
      runInAction(`Filter data loaded`, () => {
        this.data = this.data ? Object.assign({}, this.data, aggregations ?? {}) : aggregations ?? {}
      })
    } catch (err) {
      if (!axios.isCancel(err)) {
        console.error(err)
      }
    } finally {
      runInAction(`Finished loading filter data`, () => {
        this.loading = false
        this.runningQuery = false
        if (!this.hasLoaded) {
          this.hasLoaded = this.data != null
        }
      })
      //console.debug(`ES Query for ${library ? `${library} ` : ''}Filters [END]`, this.data)
    }

    // // Update local cache (which may have been created from URL) with real data from actual load
    // const {filter: {state: {groups}}} = this
    // _.keys(aggregations)
    //   .map(key => ({key, group: groups[key]}))
    //   .filter(({group}) => group.selected.size > 0)
    //   .forEach(({key, group: {selected}}) => {
    //     const remainingToSet = new Set<string>(Array.from(selected.keys()).filter(k => !selected.get(k)))
    //     for (var {key: bucketKey} of aggregations[key].items.buckets) {
    //       if (remainingToSet.has(bucketKey)) {
    //         remainingToSet.delete(bucketKey)
    //         selected.set(bucketKey, item)
    //
    //         if (remainingToSet.size == 0) {
    //           break
    //         }
    //       }
    //
    //     }
    //   })
  }

  private _setEmptyValueToLast = (data) => {
    if (data.length && data[0]?.key === '') {
      return [...data.slice(1), data[0]]
    }
    return [...data]
  }

  private convertDataToFilterItems = ({field, sectionValue, data, selectedData, groupOffline}: { field: ICatalogFieldConfig, sectionValue?, data, selectedData, groupOffline }) => {
    let r: Array<IFilterGroupItem> = [], returnCount = 0

    const prefix = sectionValue ? `${sectionValue}${COMPOUND_FILTER_SEPERATOR}` : '',
      {catalog: {groupBy: {groupingByName}, attrKeyFor, attrValueLabelFor}} = this,
      {strings, _setEmptyValueToLast, lastSearchRegex} = this,
      grouping = groupingByName.get(field.field)

    data = data?.nested ?? data
    data = data?.filterAggs ?? data

    // console.log({data, groupOffline})

    try {
      switch (field.agg) {
        case 'exists': {
          const offlineNoCount = groupOffline['false']?.length ?? 0,
            offlineYesCount = groupOffline['true']?.length ?? 0

          if (!data) throw new Error(`No ES result for ${field.field}`)
          let {doc_count: totalCount, items} = data /*: {doc_count: exists, revert}*/
          const exists = items?.revert?.doc_count ?? items?.doc_count
          totalCount = data?.total?.doc_count ?? totalCount

          r.push({sectionValue, id: true, compoundId: `${prefix}true`, name: `${prefix}Yes`, count: (items?.doc_count ?? 0) + offlineYesCount})
          r.push({sectionValue, id: false, compoundId: `${prefix}false`, name: `${prefix}No`, count: (totalCount + offlineNoCount) - (items?.doc_count ?? 0)})

          returnCount = totalCount == 0 ? 0 : (totalCount - exists) % totalCount == 0 ? 1 : 2
          break
        }

        default: {
          let {doc_count: totalCount, count, items, unassigned: unassignedObj, nested_items} = data ?? {}

          if (items?.items) items = items.items
          let {buckets: unselectedBuckets, doc_count: existsCount, sum_other_doc_count} = items ?? {buckets: [], docCount: 0}

          const unassigned = unassignedObj?.doc_count ?? 0,
            buckets = _.uniqBy([...(selectedData?.items?.buckets ?? []), ..._setEmptyValueToLast(unselectedBuckets)], d => d.key),
            offlineKeys = Object.keys(groupOffline)

          //console.log('filter buckets', toJS(field.field), buckets, groupOffline)

          _.filter(offlineKeys, k => k !== UNASSIGNED).forEach(key => {
            const f = _.find(buckets, bucket => field.def && field.def.type === 'Boolean' ? bucket.key === 1 ? key === 'true' : key === 'false' : bucket.key.toString() === key)
            if (!f) {
              buckets.push({
                sectionValue,
                compoundId: `${prefix}${key}`,
                key,
                doc_count: 0,
                node: {}
              })
            }
          })
          let nonZeroBuckets = 0
          r = buckets?.map(({key, doc_count: count, node, revert}) => {
            field?.def?.type == 'Boolean' && typeof key == 'string' ? key == 'true' ? key = 1 : key = 0 : key
            const k = field.def?.type === 'Boolean' ? key === 1 ? 'true' : 'false' : key,
              offlineCount = (groupOffline[k]?.length ?? 0),
              id = attrKeyFor({key, def: field?.def})
            let totalCount = !_.isEmpty(revert) ? ((revert?.reverse_filter?.doc_count !== undefined ? revert?.reverse_filter?.doc_count : revert?.doc_count) + offlineCount) : (count + offlineCount) // unassigned && key == 0 && field?.def?.type == 'Boolean' ? count + unassigned : count,
            if (unassigned && key == 0 && field?.def?.type == 'Boolean') {
              totalCount = totalCount + unassigned
            }
            if (totalCount) {
              nonZeroBuckets++
            }

            let topHit = node?.hits?.hits?.length ? _.get(node?.hits?.hits[0]?._source, field.field) : null
            if (_.isArray(topHit)) {
              topHit = _.first(topHit)
            }

            return {
              // For boolean attributes, treat unassigned values as 'false' for GUI purposes
              count: totalCount,
              sectionValue,
              compoundId: `${prefix}${id}`,
              id,
              name: `${prefix}${attrValueLabelFor({v: key, def: field?.def})}`,
              ...(topHit ? {
                images: _.get(topHit, `images`),
                hex: _.get(topHit, 'hex')
              } : {images: undefined, hex: undefined})
            }
          }) ?? []

          // The keys come back with '' values first in the sorted list, this code moves such items to the end of the list
          if (r.length && r[0]?.id === '') {
            r = [...r.slice(1), r[0]]
          }

          r = grouping ? grouping.applyCustomSort(r) : r
          returnCount = (buckets.length === count?.value ? nonZeroBuckets : count?.value) || 0

          if (unassigned + _.size(groupOffline?.[UNASSIGNED] ?? {}) && field?.def?.type != 'Boolean') {
            const offlineCount = groupOffline?.[UNASSIGNED]?.length ?? 0,
              name = `${prefix}${strings.unassigned}`

            if (!lastSearchRegex || name.match(lastSearchRegex)) {
              r.push({
                id: FILTER_UNASSIGNED_ID,
                compoundId: `${prefix}${FILTER_UNASSIGNED_ID}`,
                sectionValue,
                name,
                count: (unassigned + offlineCount)
              })
              returnCount += 1
            }
          }

          break
        }
      }

      return {r, returnCount}
    } catch (err) {
      onSiteError(err)
      return {r: [], returnCount: 0}
    }
  }

  // endregion

  /**
   * GVIP-449 Filter down the nested items if we have any filters that impact them
   */
  filterNested = (prefix: string, items: Array<any>, groupStore?: GroupByGroupStore, perspective?: CatalogPerspectiveConfig) => {
    return this._filterNested(prefix, items, groupStore, perspective)
  }

  _filterNested = computedFn((prefix: string, items: Array<any>, groupStore?: GroupByGroupStore, perspective?: CatalogPerspectiveConfig) => {
    if (!_.isArray(items)) { items = [items]}

    const prefixWithDot = prefix && prefix.endsWith('.') ? prefix : `${prefix}.`,
      {isToolbarFilterSwitchSelected, es, catalog, filtersWithSelections} = this,
      nestedFilters = filtersWithSelections?.filter(f => f.group.startsWith(prefixWithDot)) ?? []

    // console.log(`before`, {prefixWithDot, items})
    /**
     * Remove nested items that don't meet our group by criteria (if we're inside a group)
     */
    items = !nestedFilters.length && !groupStore
            ? items.filter(_.identity)
            : items?.filter(childData => {
        if (!childData) return false

        // If any of our groups include our nested prefix,
        if (false && groupStore) { // This behavior is likely not wanted as OTHER colorways will be hidden just because we're grouped by colorway and matching only one of them
          const groupsIncludingNested = _.keys(groupStore.keysIncludingPath).filter(k => k == prefix || k.startsWith(prefixWithDot))

          if (groupsIncludingNested.length) {
            // If we fail to match any of our group key/values filter this result

            if (!groupsIncludingNested.every(k => {
              const {val} = es.readField(childData, catalog.esFieldFor_byField(k, true).substring(prefixWithDot.length)),
                r = val === groupStore.keysIncludingPath[k] || (val === null && groupStore.keysIncludingPath[k] === false)

              return r
            })) {
              return false
            }
          }
        }

        return nestedFilters.every(({group, state: {selected, excluded}}) => {
          const {val} = es.readField(childData, group.substring(prefixWithDot.length))
          // _.difference
          // "FirstParam" : The array to inspect
          // "secondParam" : The values to exclude.
          // Fix for YFTS - 4442
          return val === null ? selected.has(FILTER_UNASSIGNED_ID) : _.isArray(val) ? _.difference(Array.from(selected.keys()), val).length == 0 : ((selected.has(val) || selected.has(val.toString()) && !(excluded.has(val) || excluded.has(val.toString()))))
        })
      })

    if (nestedFilters.length) {
      items = items.filter((childData) => {
        return nestedFilters.every(({group, state: {selected, excluded}}) => {
          const nestedField = group.substring(prefixWithDot.length),
            field = catalog._fieldByName[group],
            val = es.readField(childData, nestedField)?.val

          if (field?.agg === 'exists') {
            return !_.isEmpty(val)
          } else {
            const r = val === null ? selected.has(FILTER_UNASSIGNED_ID) : _.isArray(val) ? _.difference(Array.from(selected.keys()), val).length == 0 : ((selected.has(val) || selected.has(val.toString()) && !(excluded.has(val) || excluded.has(val.toString()))))
            return r
          }
        })
      })
    }

    if (!this.library && !_.isEmpty(catalog.features.toolbarSwitches)) {
      catalog.features.toolbarSwitches.filter(({field}) => field.startsWith(prefixWithDot)).forEach((toolbarSwitchConfig) => {
        const {field} = toolbarSwitchConfig,
          f = catalog._fieldByName[field]

        if (!isToolbarFilterSwitchSelected(toolbarSwitchConfig)) { return true } else {
          const nestedField = field.substring(prefixWithDot.length)

          items = items.filter((childData) => {
            const val = es.readField(childData, nestedField)?.val

            if (f?.agg === 'exists' || _.isArray(val)) {
              return !_.isEmpty(val)
            } else {
              return val === true
            }
          })
        }
      })
    }

    if (perspective) {
      // We've already drilled down to the array of nested objects (like secondaries)
      // Now filter them based on grouping/view option selections
      const {catalog: {sectionConfigFor, selectedViewOptionsForDynamicSection, cardView}} = this,
        section = sectionConfigFor(perspective.id),
        selectedPerspectiveViewOptions = selectedViewOptionsForDynamicSection(perspective.id)

      if (!_.isEmpty(selectedPerspectiveViewOptions)) {
        const {path, viewOptions: {field}} = section
        const key = `${path}.${field}`
        // If we're in a group that is grouping on our dynamic perspective field, filter down to only that specific item
        if (groupStore && groupStore.props.info.keys[key]) {
          const keyValue = groupStore.props.info.keys[key]
          items = items.filter(v => _.get(v, field) == keyValue)
        } else {
          // Restrict based on selected view options
          items = !selectedPerspectiveViewOptions.size ? items : items.filter(v => {
            const keyValue = _.get(v, field)
            return selectedPerspectiveViewOptions.has(keyValue)
          })
        }
      }
    }

    // console.log(`after`, {prefixWithDot, items})
    return items
  })

  @observable.ref mousedOverItem: IFilterGroupItem

  @action onFilterItem_MouseEnter = (item: IFilterGroupItem) => () => {
    this.mousedOverItem = item
  }

  @action onFilterItem_MouseLeave = (item: IFilterGroupItem) => () => {
    this.mousedOverItem = this.mousedOverItem == item ? null : this.mousedOverItem
  }

  groupFor = (field: string): Partial<FilterGroupState> => {
    if (!field) return {}

    const {state: {groups}, catalog: {_fieldByName}} = this
    let r = groups[field]
    if (r) return r

    const lastDot = field.lastIndexOf('.')

    if (lastDot != -1) {
      const parent = field.substring(0, lastDot),
        parentField = _fieldByName[parent]

      if (groups[parent] && parentField?.filter) {
        if (_.isObject(parentField.filter) && parentField.filter.subField === field.substring(lastDot + 1)) {
          return groups[parent]
        }
      }
    }
    return {}
  }

  @observable showLibraryFilterPopover: SidebarAlign | false = false
  @action onToggleShowLibraryFilterPopover = (align: SidebarAlign) => {
    if (this.showLibraryFilterPopover == align) {
      this.showLibraryFilterPopover = false
    } else {
      this.showLibraryFilterPopover = align
    }
  }

  @action closeLibraryFiltersPopover = () => {
    Array.from(this.storeByLibraryId.values()).forEach(f => f.showLibraryFilterPopover = false)
  }

  stateFor = (field: ICatalogFieldConfig | string) => {
    if (_.isString(field)) {
      field = {field}
    }

    return this.state.groups[field?.field]
  }

  isFiltered = (field: ICatalogFieldConfig, val: string) => {
    return this.state.groups[field?.field]?.selected?.has(val)
  }

  isExcluded = (field: ICatalogFieldConfig, val: string) => {
    return this.state.groups[field?.field]?.excluded?.has(val)
  }
  @observable selectedFilterGroup: string
  @action selectAndExpandFilterGroup = (field: string) => {
    this.selectedFilterGroup = field
    this.sidebar.left?.openTab(1)
    this.toggleExpandedGroup(field, true)
  }

  hasSelections = (field: string) => {
    const state = this.state.groups[field]
    if (!state) return false

    return state.selected.size > 0 || state.excluded.size > 0
  }

  sortFilterResults = (results: Array<IFilterGroupItem>, field: ICatalogFieldConfig) => {
    if (field?.sort?._func) {
      // Replace array inline
      const replacement = field.sort._func({filterItems: results, stores: this.stores})
      if (replacement.length == results.length) {
        return replacement
      } else {
        return results
      }
    }

    return _.orderBy(results, v => {
      if (v?.name === this.strings.unassigned) { return 999999} else if (field?.sort?.custom?._key) { return field.sort.custom._key.order.indexOf(v.toString()) } else {
        return _.isNumber(v.id) ? v.id
                                : _.toLower(this.catalog.labelFor(v))
      }
    })
  }

  /**
   * Modify the base requests and inject our own query terms alongside
   * https://docs.appbase.io/docs/reactivesearch/v3/overview/reactivebase/
   * @param request
   */
  onTransformReactiveBaseSearchRequest = async (request: IReactiveSearchRequest) => {
    try {
      const {body, method, url} = request
      let items = body.split('\n').filter(v => v).map(json => JSON.parse(json))

      if (items.length != 2 || !items[1]?.query) debugger

      const {filterTermsFor} = this
      items[1].query = {bool: {filter: [/*query,*/ items[1].query]}}
      console.log({body, items, method, url})
      return {...request, body: items.map(o => JSON.stringify(o)).join('\n') + '\n'}
    } catch (err) {
      onSiteError(err)
    }
    return request
  }

  dataFor = (group: string) => {
    const {data} = this
    return data ? data[group] : null
  }

  labelFor = ({group: g, value}: { group: string, value: any }) => {
    const {strings, catalog: {_fieldByName}, groupFor} = this,
      group = groupFor(g)

    if (group?.def?.type == 'Boolean') {
      const {def: {booleanLabels}} = group
      return value === 'true' ? booleanLabels?.trueLabel || 'True'
                              : value === 'false' ? booleanLabels?.falseLabel || 'False' : value
    }

    let r = group?.def?.renderer === 'percentage' && value !== UNASSIGNED ?
            Number(parseFloat(value) * (1)).toLocaleString(undefined, {style: 'percent', maximumFractionDigits: 0})
                                                                          : value != null || typeof value == 'number' || value === '' ? value.toString() : 'None'
    group?.def?.precision && !_.isNaN(Number(r)) && (r = Number(r).toLocaleString(undefined, {maximumFractionDigits: group?.def?.precision}))
    return r === '' ? strings.noValue : r
  }
}

export interface IReactiveSearchRequest {
  url: string
  method: string | 'GET' | 'POST'
  body: string
}



// WEBPACK FOOTER //
// ./src/modules/catalog/internal/filters/FilterStore.ts